import engineApiSlice from 'src/shared/state/api-slices/engineApiSlice';

export type RiskInsightsSiteStatus = 'needs-sync' | 'uptodate';

export type SiteStatusItem = {
    readonly site_id: string;
    readonly status: RiskInsightsSiteStatus;
};

const needsSyncIdSet = (items: ReadonlyArray<SiteStatusItem>): Set<string> =>
    new Set(items.filter(item => item.status === 'needs-sync').map(item => item.site_id));

export const countNeedsSync = (items: ReadonlyArray<SiteStatusItem>): number => needsSyncIdSet(items).size;

export const hasSiteStatusChangedSince = (snapshot: ReadonlyArray<SiteStatusItem>, current: ReadonlyArray<SiteStatusItem>): boolean => {
    const before = needsSyncIdSet(snapshot);
    const after = needsSyncIdSet(current);

    return before.size !== after.size || [...after].some(id => !before.has(id));
};

export type SiteStatusResponse = {
    readonly items: ReadonlyArray<SiteStatusItem>;
};

export type RiskInsightsRunScope = 'all' | 'needs_data_refresh';

export type SubmitRiskInsightsRequest = {
    businessCentralId: string;
    scope: RiskInsightsRunScope;
};

export type SubmitRiskInsightsResponse = {
    readonly run_id: string;
};

const SITE_STATUS_TAG = { type: 'riskInsightsSiteStatus', id: 'LIST' } as const;

const riskInsightsRefreshEngineSlice = engineApiSlice.injectEndpoints({
    endpoints: builder => ({
        getRiskInsightsSiteStatus: builder.query<SiteStatusResponse, { businessCentralId: string }>({
            query: ({ businessCentralId }) => ({
                url: `/org/${businessCentralId}/risk-insights/site-status`,
                method: 'GET',
            }),
            providesTags: [SITE_STATUS_TAG],
        }),
        submitRiskInsights: builder.mutation<SubmitRiskInsightsResponse, SubmitRiskInsightsRequest>({
            query: ({ businessCentralId, scope }) => ({
                url: `/org/${businessCentralId}/risk-insights/submit`,
                method: 'POST',
                body: { scope },
            }),
            invalidatesTags: [SITE_STATUS_TAG],
        }),
    }),
});

export const { useGetRiskInsightsSiteStatusQuery, useSubmitRiskInsightsMutation } = riskInsightsRefreshEngineSlice;

export default riskInsightsRefreshEngineSlice;
